import { useEffect, useState } from "react";
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import {
  IconCertificate,
  IconChevronRight,
  IconCloudUpload,
  IconFileSpreadsheet,
  IconLanguage,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  getClassificationStandards,
  getClassificationClasses,
  getClassificationClass,
  importEtimCsv,
  importIxfTranslations,
} from "../api";

export default function ClassificationStandards() {
  const { t } = useTranslation("common");
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [version, setVersion] = useState("10.0");

  // IXF language overlay
  const [ixfStandardId, setIxfStandardId] = useState(null);
  const [ixfLanguages, setIxfLanguages] = useState("");
  const [ixfImporting, setIxfImporting] = useState(false);
  const [ixfReport, setIxfReport] = useState(null);

  // Browser state
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]); // [{code, label}]
  const [selectedClass, setSelectedClass] = useState(null); // {classInfo, features}
  const [classLoading, setClassLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const loadStandards = async () => {
    try {
      const data = await getClassificationStandards();
      setStandards(data);
    } catch {
      // api.js shows error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStandards();
  }, []);

  const handleImport = async (files) => {
    const file = files[0];
    if (!file) return;

    setImporting(true);
    try {
      const result = await importEtimCsv(file, version);
      notifications.show({
        title: t("admin.standards.etimImportComplete"),
        message: t("admin.standards.etimImportCompleteMessage", {
          code: result.code,
          version: result.version,
          classCount: result.classCount,
          featureCount: result.featureCount,
        }),
        color: "green",
      });
      await loadStandards();
    } catch (err) {
      notifications.show({
        title: t("admin.standards.etimImportFailed"),
        message: err.message,
        color: "red",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleIxfImport = async (files) => {
    const file = files[0];
    if (!file || !ixfStandardId) return;

    setIxfImporting(true);
    setIxfReport(null);
    try {
      const report = await importIxfTranslations(
        ixfStandardId,
        file,
        ixfLanguages
      );
      setIxfReport(report);
      notifications.show({
        title: t("admin.standards.ixfApplied", { release: report.release }),
        message: t("admin.standards.ixfAppliedMessage", {
          languages:
            report.languages.join(", ") ||
            t("admin.standards.ixfNoNewLanguages"),
        }),
        color: "green",
      });
      await loadStandards();
    } catch (err) {
      notifications.show({
        title: t("admin.standards.ixfImportFailed"),
        message: err.message,
        color: "red",
      });
    } finally {
      setIxfImporting(false);
    }
  };

  const browseStandard = async (standard) => {
    setSelectedStandard(standard);
    setSelectedClass(null);
    setBreadcrumbs([]);
    setSearchQuery("");
    setClassesLoading(true);
    try {
      const data = await getClassificationClasses(standard.id);
      setClasses(data);
    } catch {
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  const browseChildren = async (cls) => {
    if (!cls.hasChildren) {
      // Leaf class — load features
      setClassLoading(true);
      try {
        const data = await getClassificationClass(selectedStandard.id, cls.code);
        setSelectedClass(data);
      } catch {
        setSelectedClass(null);
      } finally {
        setClassLoading(false);
      }
      return;
    }

    // Has children — drill down
    setBreadcrumbs((prev) => [
      ...prev,
      { code: cls.code, label: cls.labels?.en || cls.code },
    ]);
    setSelectedClass(null);
    setSearchQuery("");
    setClassesLoading(true);
    try {
      const data = await getClassificationClasses(
        selectedStandard.id,
        cls.code
      );
      setClasses(data);
    } catch {
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  const navigateBreadcrumb = async (index) => {
    // -1 = root, 0+ = breadcrumb index
    const newBreadcrumbs = index < 0 ? [] : breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setSelectedClass(null);
    setSearchQuery("");
    setClassesLoading(true);
    try {
      const parentCode =
        newBreadcrumbs.length > 0
          ? newBreadcrumbs[newBreadcrumbs.length - 1].code
          : null;
      const data = await getClassificationClasses(
        selectedStandard.id,
        parentCode
      );
      setClasses(data);
    } catch {
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) {
      // Reset to current breadcrumb level
      const parentCode =
        breadcrumbs.length > 0
          ? breadcrumbs[breadcrumbs.length - 1].code
          : null;
      setClassesLoading(true);
      try {
        const data = await getClassificationClasses(
          selectedStandard.id,
          parentCode
        );
        setClasses(data);
      } catch {
        setClasses([]);
      } finally {
        setClassesLoading(false);
      }
      return;
    }

    setClassesLoading(true);
    try {
      const data = await getClassificationClasses(
        selectedStandard.id,
        null,
        q
      );
      setClasses(data);
    } catch {
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  return (
    <Container size="lg" py="xl">
      <Group mb="xl" gap="sm">
        <IconCertificate size={28} />
        <Title order={2}>{t("admin.standards.title")}</Title>
      </Group>

      {/* Standards list */}
      <Paper withBorder p="md" radius="md" mb="xl">
        <Title order={4} mb="sm">
          {t("admin.standards.importedStandards")}
        </Title>
        {loading ? (
          <Loader size="sm" />
        ) : standards.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("admin.standards.noStandards")}
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin.standards.colStandard")}</Table.Th>
                <Table.Th>{t("admin.standards.colVersion")}</Table.Th>
                <Table.Th>{t("admin.standards.colClasses")}</Table.Th>
                <Table.Th>{t("admin.standards.colFeatures")}</Table.Th>
                <Table.Th>{t("admin.standards.colStatus")}</Table.Th>
                <Table.Th>{t("admin.standards.colImported")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {standards.map((s) => (
                <Table.Tr
                  key={s.id}
                  style={{ cursor: "pointer" }}
                  bg={
                    selectedStandard?.id === s.id
                      ? "var(--mantine-color-blue-0)"
                      : undefined
                  }
                  onClick={() => browseStandard(s)}
                >
                  <Table.Td fw={600}>{s.name}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      v{s.version}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{s.classCount.toLocaleString()}</Table.Td>
                  <Table.Td>{s.featureCount.toLocaleString()}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={s.status === "Active" ? "green" : "yellow"}
                      variant="light"
                      size="sm"
                    >
                      {s.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(s.importedAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Standard browser */}
      {selectedStandard && (
        <Paper withBorder p="md" radius="md" mb="xl">
          <Group justify="space-between" mb="sm">
            <div>
              <Title order={4}>
                {selectedStandard.name} v{selectedStandard.version}
              </Title>
              <Breadcrumbs separator={<IconChevronRight size={12} />} mt={4}>
                <Anchor
                  size="sm"
                  onClick={() => navigateBreadcrumb(-1)}
                  c={breadcrumbs.length === 0 ? "dimmed" : undefined}
                >
                  {t("admin.standards.root")}
                </Anchor>
                {breadcrumbs.map((bc, i) => (
                  <Anchor
                    key={bc.code}
                    size="sm"
                    onClick={() => navigateBreadcrumb(i)}
                    c={i === breadcrumbs.length - 1 ? "dimmed" : undefined}
                  >
                    {bc.label}
                  </Anchor>
                ))}
              </Breadcrumbs>
            </div>
            <TextInput
              placeholder={t("admin.standards.searchClasses")}
              size="sm"
              w={250}
              leftSection={<IconSearch size={14} />}
              value={searchQuery}
              onChange={(e) => handleSearch(e.currentTarget.value)}
            />
          </Group>

          {classesLoading ? (
            <Loader size="sm" />
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="15%">{t("admin.standards.colCode")}</Table.Th>
                  <Table.Th>{t("admin.standards.colName")}</Table.Th>
                  <Table.Th w="10%">{t("admin.standards.colFeatures")}</Table.Th>
                  <Table.Th w="8%">{t("admin.standards.colLevel")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {classes.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed" size="sm" ta="center">
                        {t("admin.standards.noClasses")}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  classes.map((cls) => (
                    <Table.Tr
                      key={cls.id}
                      style={{ cursor: "pointer" }}
                      bg={
                        selectedClass?.classInfo?.code === cls.code
                          ? "var(--mantine-color-blue-0)"
                          : undefined
                      }
                      onClick={() => browseChildren(cls)}
                    >
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {cls.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Text size="sm">{cls.labels?.en || cls.code}</Text>
                          {cls.hasChildren && (
                            <IconChevronRight
                              size={14}
                              color="var(--mantine-color-dimmed)"
                            />
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {cls.hasFeatures && (
                          <Badge size="xs" variant="light">
                            {t("admin.standards.featuresBadge")}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="xs"
                          variant="outline"
                          color="gray"
                        >
                          L{cls.level}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          )}

          {/* Feature detail panel */}
          {classLoading && <Loader size="sm" mt="md" />}
          {selectedClass && (
            <>
              <Divider my="md" />
              <Title order={5} mb="xs">
                {t("admin.standards.classFeaturesHeading", {
                  name:
                    selectedClass.classInfo?.labels?.en ||
                    selectedClass.classInfo?.code,
                })}
              </Title>
              <Table size="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w="12%">{t("admin.standards.colCode")}</Table.Th>
                    <Table.Th>{t("admin.standards.colFeature")}</Table.Th>
                    <Table.Th w="10%">{t("admin.standards.colType")}</Table.Th>
                    <Table.Th w="8%">{t("admin.standards.colUnit")}</Table.Th>
                    <Table.Th>{t("admin.standards.colAllowedValues")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedClass.features?.map((f) => (
                    <Table.Tr key={f.code}>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {f.code}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{f.labels?.en || f.code}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" variant="light" color="gray">
                          {f.featureType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {f.unitLabel?.en || f.unitCode || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {f.allowedValues?.length > 0 ? (
                          <Group gap={3} wrap="wrap">
                            {f.allowedValues.slice(0, 8).map((v) => (
                              <Tooltip key={v.code} label={v.code}>
                                <Badge
                                  size="xs"
                                  variant="outline"
                                  color="gray"
                                  tt="none"
                                >
                                  {v.labels?.en || v.code}
                                </Badge>
                              </Tooltip>
                            ))}
                            {f.allowedValues.length > 8 && (
                              <Text size="xs" c="dimmed">
                                {t("diagnose.report.moreCount", {
                                  count: f.allowedValues.length - 8,
                                })}
                              </Text>
                            )}
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </>
          )}
        </Paper>
      )}

      {/* CSV import */}
      <Paper withBorder p="md" radius="md">
        <Title order={4} mb="xs">
          {t("admin.standards.importEtimTitle")}
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          {t("admin.standards.importEtimDescription")}
        </Text>

        <Group mb="md" align="flex-end">
          <TextInput
            label={t("admin.standards.etimVersion")}
            value={version}
            onChange={(e) => setVersion(e.currentTarget.value)}
            w={120}
            size="sm"
          />
        </Group>

        <Dropzone
          onDrop={handleImport}
          accept={[MIME_TYPES.csv, "text/csv"]}
          maxSize={100 * 1024 ** 2}
          loading={importing}
          multiple={false}
        >
          <Group
            justify="center"
            gap="xl"
            mih={100}
            style={{ pointerEvents: "none" }}
          >
            <Dropzone.Accept>
              <IconCloudUpload size={40} color="var(--mantine-color-blue-6)" />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} color="var(--mantine-color-red-6)" />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFileSpreadsheet
                size={40}
                color="var(--mantine-color-dimmed)"
              />
            </Dropzone.Idle>

            <Stack gap={4}>
              <Text size="lg" fw={500}>
                {importing
                  ? t("admin.standards.importing")
                  : t("admin.standards.dropCsv")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("admin.standards.csvHint")}
              </Text>
            </Stack>
          </Group>
        </Dropzone>
      </Paper>

      {/* IXF language overlay */}
      <Paper withBorder p="md" radius="md" mt="md">
        <Title order={4} mb="xs">
          {t("admin.standards.ixfTitle")}
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          {t("admin.standards.ixfDescription")}
        </Text>

        <Group mb="md" align="flex-end">
          <Select
            label={t("admin.standards.applyToStandard")}
            placeholder={t("admin.standards.pickStandard")}
            data={standards
              .filter((s) => s.code === "ETIM")
              .map((s) => ({ value: s.id, label: `${s.code} ${s.version}` }))}
            value={ixfStandardId}
            onChange={setIxfStandardId}
            w={240}
            size="sm"
          />
          <TextInput
            label={t("admin.standards.languagesLabel")}
            description={t("admin.standards.languagesHint")}
            placeholder="de,nl"
            value={ixfLanguages}
            onChange={(e) => setIxfLanguages(e.currentTarget.value)}
            w={200}
            size="sm"
          />
        </Group>

        <Dropzone
          onDrop={handleIxfImport}
          accept={["text/xml", "application/xml"]}
          maxSize={200 * 1024 ** 2}
          loading={ixfImporting}
          disabled={!ixfStandardId}
          multiple={false}
        >
          <Group
            justify="center"
            gap="xl"
            mih={100}
            style={{ pointerEvents: "none" }}
          >
            <Dropzone.Accept>
              <IconCloudUpload size={40} color="var(--mantine-color-blue-6)" />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} color="var(--mantine-color-red-6)" />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconLanguage size={40} color="var(--mantine-color-dimmed)" />
            </Dropzone.Idle>

            <Stack gap={4}>
              <Text size="lg" fw={500}>
                {ixfImporting
                  ? t("admin.standards.applyingTranslations")
                  : t("admin.standards.dropIxf")}
              </Text>
              <Text size="sm" c="dimmed">
                {t("admin.standards.ixfFileHint")}
              </Text>
            </Stack>
          </Group>
        </Dropzone>

        <Text size="xs" c="dimmed" mt="sm">
          {t("admin.standards.licensePrefix")}{" "}
          <Anchor
            href="http://opendatacommons.org/licenses/by/1.0/"
            target="_blank"
            rel="noreferrer"
            size="xs"
          >
            {t("admin.standards.licenseLinkLabel")}
          </Anchor>
          {t("admin.standards.licenseSuffix")}
        </Text>

        {ixfReport && (
          <Table mt="md" withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin.standards.colEntity")}</Table.Th>
                <Table.Th>{t("admin.standards.colInFile")}</Table.Th>
                <Table.Th>{t("admin.standards.colApplied")}</Table.Th>
                <Table.Th>{t("admin.standards.colStale")}</Table.Th>
                <Table.Th>{t("admin.standards.colAlreadyOfficial")}</Table.Th>
                <Table.Th>{t("admin.standards.colNotInFile")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Object.entries(ixfReport.kinds || {}).map(([kind, s]) => (
                <Table.Tr key={kind}>
                  <Table.Td>{kind}</Table.Td>
                  <Table.Td>{s.inFile}</Table.Td>
                  <Table.Td>{s.applied}</Table.Td>
                  <Table.Td>{s.stale}</Table.Td>
                  <Table.Td>{s.alreadyOfficial}</Table.Td>
                  <Table.Td>{s.notInFile}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Container>
  );
}
